import * as Sentry from '@sentry/react'
import i18next from 'i18next'
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next, useTranslation } from 'react-i18next'
import i18nextConfig from '../locales/config'

//EVERY language file must be previously imported here, since the native app can't lazy-load JS chunks
//mild TODO: why can't it? It seems Cordova force-changes any relative path to include the full URL, but since lazy-loaded scripts come later, they're not affected by that... but can't we make the lazy-loaded absolute as well, maybe using window.ROOT_URL?
//TODO can we import a simpler version of en-US which only includes the different strings? otherwise, we're adding >30kb of duplicated text
import '../locales/en-US/translation.json' //needed because of the simplified CONTACT_* strings
import '../locales/en-CA/translation.json'
import '../locales/zh-CN/translation.json'



class Lang {

	code
	name

	constructor(code, name) {
		this.code = code
		this.name = name
	}
}

/**
 * Encapsulates the several ways in which the i18next object is used:
 * - configuration is entirely done in a single method, avoiding to expose that in, say, the root component
 * - single source of truth for available languages and language heuristics - to select between main language and dialects, for instance
 * - no need to deal directly with the i18next object directly. For instance, this simplifies getting the current language, which is a bit complex since it has TWO attributes for that.
 */
export default class Locale {

	/** Enables logging internal implementations and "our" missing keys */
	static #DEBUG = false // platform.isDev()

	static #fallbackMemo = []

	// t('English (USA)')
	// t('English (Canada)')
	// t('Chinese (Simplified)')
	/**
	 * List of languages supported by the UI.
	 * Locale files are generated based on `parser.locales` at locales/config.js, and general languages are listed in `supportedLngs` at the same file. Don't forget to update these as well!
	 * @type Lang[]
	 */
	#available = ([
		new Lang(Locale.DEFAULT, 'English (USA)'),
		new Lang('en-CA', 'English (Canada)'),
		new Lang('zh-CN', 'Chinese (Simplified)'),
	])

	/** @return Lang[] */
	get AVAILABLE() { return this.#available }
	static get DEFAULT() { return 'en-US' }
	static isDefault = (lang) => ['en', 'en-US'].includes(lang)
	static isSibling = (lang1, lang2) => lang1.split('-')[0] == lang2.split('-')[0]

	/** @return {string} The lang code */
	current = () => i18next.resolvedLanguage //TODO not sure why this is an instance method?? So I created a static below
	static currentLang = () => i18next.resolvedLanguage

	/**
	 * A hook that returns the current language, auto-updating when it changes.
	 * @note This could be used for `HTML[lang]`, but going directly to the source is way faster in that scenario - see {@link setHtmlLang()}.
	 * @returns {string}
	 */
	static useLang = () => {
		//while ESLint complains about the Rules of Hooks, this is NOT a class component, just a bunch of encapsulated functions. So just STFU and let us do our syntactic sugar.
		const { i18n } = useTranslation() // eslint-disable-line react-hooks/rules-of-hooks
		return i18n.resolvedLanguage
	}

	/**
	 * Sets the language in the various places pertaining to the client code.
	 * @internal If the user requested a language change, you should use {@link saveLocale()} - which calls this and also requests that change to the backend.
	 * @see saveLocale()
	 * @param lang
	 * @returns {Promise<void>}
	 */
	set = async (lang) => {
		await i18next.changeLanguage(lang)
		Sentry.setContext('culture', { locale: Locale.currentLang() })
		setHtmlLang()
	}

	static log(msg, ...stuff) {
		if (Locale.#DEBUG) {
			let level = 'debug'
			if (typeof msg == 'object') {
				level = msg.level
				msg   = msg.msg
			}
			console[level](`[i18n] ${msg}`, ...stuff)
		}
	}

	/**
	 * Looks for other supported languages with the same base code as the given language.
	 * Skips the default locale, as that's always the last option for fallback:
	 *   1. it's better to offer other English dialects before offering USA to anglophones[1]
	 *   2. it's also last resort for every other locale that might be requested.
	 * E.g. could return [en-GB, en-CA] for en-AU
	 * @see [1] //https://en.wikipedia.org/wiki/British_English#/media/File:International_English_Spelling.png
	 * @param lang
	 * @return string[]
	 */
	static findSiblings(lang) {
		const skip   = [lang, Locale.DEFAULT]
		const [base] = lang.split('-')
		return (new Locale()).AVAILABLE.map(l => l.code).filter(code => code.startsWith(base) && !skip.includes(code))
	}

	static async configure() {
		await i18next
			.use(initReactI18next)
			.use(I18nextBrowserLanguageDetector) //TODO: could be replaced by our own backend logic via API call
			.use(resourcesToBackend((lang, ns, callback) => { //cache+LS still doesn't solve race condition
				if (lang.length == 2) {
					//I guess we only fall here because of nonExplicitSupportedLngs:true, but that's tied to supportedLngs...
					callback(null, null)
				} else {
					// Locale.log(`Loading language file: ${lang}/${ns}`)
					import(`../locales/${lang}/${ns}.json`) //those files ALSO need to be previously imported at the start!
						.then(res => callback(null, res))
						.catch(callback)
				}
			}))
			.init(Object.assign(i18nextConfig, {
				debug: Locale.#DEBUG, //Make sure you set "-missingKey" in your console filter, otherwise you'll get tons of notices for the fallback keys

				/**
				 * Memoizes Locale.fallbackMemo + turns the default into false, as i18next asks
				 * @see https://www.i18next.com/principles/fallback
				 */
				fallbackLng: requested => {
					if (!requested) { //not sure why would it request a fallback for nothing, but docs won't explain it either
						return Locale.DEFAULT
					}

					//returns any other dialect as a fallback; for instance, we can still offer pt-BR for Portuguese people before falling back to en-US. i18next's default behavior is to offer root languages, which in practice doesn't exist, making it hard to set as options
					if (!(requested in Locale.#fallbackMemo)) {
						Locale.#fallbackMemo[requested] = Locale.findSiblings(requested)
						Locale.#fallbackMemo[requested].push(Locale.DEFAULT)
						Locale.log('fallbacks selected for', requested, Locale.#fallbackMemo[requested])
					}
					return Locale.#fallbackMemo[requested]
				},

				//saveMissing is not related to missingKey logging: https://github.com/i18next/i18next/issues/1962
				saveMissing: true,
				missingKeyHandler: (lang, ns, key, fallbackValue, isUpdate, options) => {
					if (!Locale.isDefault(lang[0])) {
						Locale.log({ level: 'debug', msg: `Missing ${lang}: ${key} ${options.context || ''}` })
					}
					return fallbackValue
				},
			}))
			.then(setHtmlLang)
	}

}

function setHtmlLang() {
	document.documentElement.lang = Locale.currentLang()
}
